import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import React from 'react';

import Panel from 'react-bootstrap/lib/Panel';

import {
  faRocket,
  faRuler,
  faList,
  faChartLine,
  faDollarSign,
} from '@fortawesome/free-solid-svg-icons';

import Link from '../../../../../components/Link';
import PageLayout from '../../../../../components/PageLayout';

import ShadowerLink from '../../../../../components/ShadowerLink';

import Navbar from '../../../../../components/Handbook/Navbar';

import s from '../../HandbookPage.scss';

class OptimizingYourProfilePage extends React.Component {
  render() {
    return (
      <PageLayout className={s.page}>
        <div className={s.container}>
          <div className={s.navbar}>
            <div className={s.title}>Shadowing Handbook</div>
            <Navbar
              navbarIcon={faRocket}
              navbarSection="Getting Started"
              navbarSubsection={
                <div>
                  <Link
                    className={s.link}
                    to="/handbook/getting-started/welcome-to-shadower"
                  >
                    Welcome to Shadower
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/getting-started/how-shadower-works"
                  >
                    How Shadower Works
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/getting-started/working-with-businesses"
                  >
                    Working with Businesses
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/getting-started/shadowing-guidelines"
                  >
                    Shadowing Guidelines
                  </Link>
                </div>
              }
            />

            <Navbar
              navbarIcon={faRuler}
              navbarSection="Planning a Session"
              navbarSubsection={
                <div>
                  <Link
                    className={s.link}
                    to="/handbook/planning-a-session/choosing-a-topic"
                  >
                    Choosing a Topic
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/planning-a-session/interactivity-level"
                  >
                    Interactivity Level
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/planning-a-session/session-type"
                  >
                    Session Type
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/planning-a-session/providing-resources"
                  >
                    Providing Resources
                  </Link>
                </div>
              }
            />

            <Navbar
              navbarIcon={faList}
              navbarSection="Managing a Session"
              navbarSubsection={
                <div>
                  <Link
                    className={s.link}
                    to="/handbook/managing-a-session/expectations"
                  >
                    Expectations
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/managing-a-session/session-flow"
                  >
                    Session Flow
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/managing-a-session/maximizing-your-time"
                  >
                    Maximizing Your Time
                  </Link>
                </div>
              }
            />

            <Navbar
              navbarIcon={faChartLine}
              navbarSection="Promoting Your Profile"
              navbarSubsection={
                <div>
                  <Link
                    className={s.link}
                    to="/handbook/promoting-your-profile/how-the-platform-works"
                  >
                    How the Platform Works
                  </Link>
                  <Link
                    className={cx(s.link, s.active)}
                    to="/handbook/promoting-your-profile/optimizing-your-profile"
                  >
                    Optimizing Your Profile
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/promoting-your-profile/marketing-yourself"
                  >
                    Marketing Yourself
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/promoting-your-profile/how-to-build-a-following"
                  >
                    How to Build a Following
                  </Link>
                </div>
              }
              overrideDefault
            />

            <Navbar
              navbarIcon={faDollarSign}
              navbarSection="Earning Revenue"
              navbarSubsection={
                <div>
                  <Link
                    className={s.link}
                    to="/handbook/earning-revenue/how-payment-works"
                  >
                    How Payment Works
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/earning-revenue/connecting-with-the-community"
                  >
                    Connecting with the Community
                  </Link>
                </div>
              }
            />

            <Link className={s.newProfileLink} to="/user/profiles/new">
              Create a shadowing profile
            </Link>
          </div>

          <div className={s.explanation}>
            <Panel>
              <Panel.Heading>
                <div>Promoting Your Profile</div>
              </Panel.Heading>
              <Panel.Body>
                <div className={s.handbookTitle}>A guide for experts</div>
                <div className={s.subsectionTitle}>Optimizing Your Profile</div>

                <p>
                  Optimizing your profile for discovery can help you begin
                  earning stable, supplemental income.
                </p>

                <div className={s.contentContainer}>
                  <div>
                    <strong>Optimizing your shadowing profile</strong>
                  </div>
                  <p>
                    When filling out your shadowing profile, pay special
                    attention to your:
                  </p>

                  <ul>
                    <li>
                      Profile's Name
                      <p>
                        A great profile name explains what shadowers can learn
                        from you in a nutshell. For example, instead of "Music
                        Production," try being more specific with the name of a
                        genre or artist like "Future Bass Production" or "Future
                        Bass like Illenium."
                      </p>
                    </li>

                    <li>
                      Profile's Media
                      <p>
                        The media on your shadowing profile should provide a
                        clear depiction of what shadowing you is like. We
                        recommend high-quality images and videos that show off
                        your work, your studio, you projects and your
                        personality.
                      </p>
                    </li>

                    <li>
                      Profile's Categorization
                      <p>
                        You can add up to two categories and subcategories to
                        every shadowing profile. Many users primarily search by
                        these subcategories.
                      </p>
                    </li>

                    <li>
                      Profile's Keywords
                      <p>
                        When a user searches for a specific skill, topic or
                        tool, the platform searches three specific areas of your
                        shadowing profile to finding a match: the{' '}
                        <b>Tools and Equipment</b> section, your{' '}
                        <b>Specialties</b> section and each of your individual
                        session titles. Be sure to include detailed information
                        in those areas.
                      </p>
                    </li>
                  </ul>
                </div>

                <div className={s.contentContainer}>
                  <div>
                    <strong>Optimizing your individual sessions</strong>
                  </div>
                  <p>
                    Within your shadowing profile, you can create as many unique
                    shadowing sessions as you want. To ensure your sessions
                    reach the widest audience possible, we recommend that you:
                  </p>

                  <ul>
                    <li>
                      Create multiple different sessions
                      <p>
                        Your sessions should encompass a variety of topics
                        related to your area of expertise. And remember,
                        specificity is essential to make sure your profile
                        appears in user searches. For example, instead of
                        titling every session "EDM production," try breaking
                        that into "EDM for beginners: getting started producing"
                        "Advanced EDM: mixing and mastering like the pros."
                      </p>
                      <p>
                        Session titles are also a great place to highlight a
                        specific piece of technology that you use like "Mixing
                        house in Ableton" or "Producing future bass in Logic
                        Pro."
                      </p>
                    </li>

                    <li>
                      Allow for private or public sessions
                      <p>
                        If you allow multiple shadowers per session, consider
                        testing whether public or private sessions get the best
                        response. Read more about the benefits of public versus
                        private sessions in the{' '}
                        <Link to="/help">Help Center</Link>.
                      </p>
                    </li>

                    <li>
                      Test various prices
                      <p>
                        Typically interactive sessions cost more, but it's worth
                        playing around with session prices to find a comfortable
                        medium between what your time is worth and what a
                        shadower is willing to pay.
                      </p>
                    </li>

                    <li>
                      Vary the interactivity
                      <p>
                        Consider also providing sessions with varying amount of
                        interactivity. Some shadowers may simply want to see
                        what you do for a low price. Others may want to work
                        with you one-on-one on a large project and would be
                        willing to pay more for the extra time and attention.
                      </p>
                    </li>
                  </ul>
                </div>

                <ShadowerLink
                  className={s.nextLink}
                  to="/handbook/promoting-your-profile/marketing-yourself"
                >
                  Next {'>'}
                </ShadowerLink>
              </Panel.Body>
            </Panel>
          </div>
        </div>
      </PageLayout>
    );
  }
}

export default withStyles(s)(OptimizingYourProfilePage);
